import axios from 'axios';
import { io } from 'socket.io-client';
import api from './api';

const normalizeApiUrl = (rawUrl) => {
  const clean = String(rawUrl || '').trim().replace(/\/+$/, '');
  if (!clean) return '';
  return /\/api$/i.test(clean) ? clean : `${clean}/api`;
};

const baseURL = normalizeApiUrl(process.env.REACT_APP_API_URL)
  || ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:5000/api'
    : 'https://gestion.smtlinkss.com/api');

const partnerApi = axios.create({ baseURL: `${baseURL}/partner-portal` });
const socketURL = baseURL.replace(/\/api$/i, '');
partnerApi.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('partnerPortalToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.data instanceof FormData) delete config.headers['Content-Type'];
  return config;
});
partnerApi.interceptors.response.use((response) => response, (error) => {
  if (error.response?.status === 401) {
    sessionStorage.removeItem('partnerPortalToken');
    sessionStorage.removeItem('partnerPortalUser');
  }
  return Promise.reject(error);
});

const appendFiles = (formData, files = []) => files.forEach((file) => formData.append('files', file));
const toPreviewPayload = (response, file) => ({
  blob: response.data,
  mimeType: response.headers?.['content-type'] || file.mime_type || response.data?.type || '',
  name: file.original_name || 'document'
});
const downloadBlob = (blob, fileName = 'document') => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const partnerQuoteService = {
  login: async (email, password) => {
    const response = await partnerApi.post('/auth/login', { email, password });
    sessionStorage.setItem('partnerPortalToken', response.data.token);
    sessionStorage.setItem('partnerPortalUser', JSON.stringify(response.data.partner));
    return response.data;
  },
  logout: () => {
    sessionStorage.removeItem('partnerPortalToken');
    sessionStorage.removeItem('partnerPortalUser');
  },
  createRealtimeSocket: () => io(socketURL, {
    auth: { token: sessionStorage.getItem('partnerPortalToken') },
    reconnection: true,
    reconnectionAttempts: 8,
    reconnectionDelay: 800,
    reconnectionDelayMax: 5000,
    transports: ['websocket', 'polling']
  }),
  getMe: async () => (await partnerApi.get('/me')).data,
  listThreads: async () => (await partnerApi.get('/threads')).data,
  getThread: async (id) => (await partnerApi.get(`/threads/${id}`)).data,
  createThread: async (values, files) => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => formData.append(key, value ?? ''));
    appendFiles(formData, files);
    return (await partnerApi.post('/threads', formData)).data;
  },
  addMessage: async (threadId, message, files) => {
    const formData = new FormData();
    formData.append('message', message || '');
    appendFiles(formData, files);
    return (await partnerApi.post(`/threads/${threadId}/messages`, formData)).data;
  },
  deletePartnerThread: async (threadId) => (await partnerApi.delete(`/threads/${threadId}`)).data,
  getPartnerFilePreview: async (threadId, file) => {
    const response = await partnerApi.get(`/threads/${threadId}/files/${file.id}`, { responseType: 'blob' });
    return toPreviewPayload(response, file);
  },
  listAccounts: async () => (await api.get('/partner-portal/internal/accounts')).data,
  createAccount: async (payload) => (await api.post('/partner-portal/internal/accounts', payload)).data,
  updateAccount: async (id, payload) => (await api.patch(`/partner-portal/internal/accounts/${id}`, payload)).data,
  listInternalThreads: async (params = {}) => (await api.get('/partner-portal/internal/threads', { params })).data,
  createInternalThread: async (values, files) => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => formData.append(key, value ?? ''));
    appendFiles(formData, files);
    return (await api.post('/partner-portal/internal/threads', formData)).data;
  },
  getInternalThread: async (id) => (await api.get(`/partner-portal/internal/threads/${id}`)).data,
  addInternalMessage: async (threadId, message, files) => {
    const formData = new FormData();
    formData.append('message', message || '');
    appendFiles(formData, files);
    return (await api.post(`/partner-portal/internal/threads/${threadId}/messages`, formData)).data;
  },
  updateThread: async (id, payload) => (await api.patch(`/partner-portal/internal/threads/${id}`, payload)).data,
  deleteInternalThread: async (id) => (await api.delete(`/partner-portal/internal/threads/${id}`)).data,
  getInternalFilePreview: async (threadId, file) => {
    const response = await api.get(`/partner-portal/internal/threads/${threadId}/files/${file.id}`, { responseType: 'blob' });
    return toPreviewPayload(response, file);
  },
  downloadPreview: (preview) => downloadBlob(preview.blob, preview.name)
};

export default partnerQuoteService;
